import type { TFunction } from "i18next";
import { ClientContextErrorCodes } from "@nexus/contracts";
import { TenantOnboardingJobError } from "@/features/identity-hub/api/identity-hub.api";
import { ApiError, getLocalUserFacingApiMessage } from "@/lib/api-client";
import { AmberWorkerFailureError } from "@/lib/use-amber-mutation";

/**
 * Why an onboarding create or update was not saved; `field` is set when one field was rejected, and
 * `isGeneral` when the reason is not shown, so the screen may already say all there is to say.
 */
export interface OnboardingSaveError {
  message: string;
  field?: string;
  isGeneral?: boolean;
}

/** Failures whose message was written for the form: a field the setup schema or the client-context API rejected. */
const FIELD_MESSAGE_CODES: ReadonlySet<string> = new Set([
  ClientContextErrorCodes.VALIDATION_FAILED,
  ClientContextErrorCodes.API_REJECTED,
]);

/**
 * Failures the user can act on, each with its own plain message. Every other worker failure (a
 * BigQuery read-back, an integrity check, an unexpected error) carries technical detail meant for the
 * logs, so the user sees the general message instead.
 */
const USER_FACING_SAVE_ERRORS: Partial<Record<string, { key: string; defaultValue: string }>> = {
  [ClientContextErrorCodes.ALREADY_EXISTS]: {
    key: "iam.onboarding.errors.alreadyExists",
    defaultValue: "Onboarding already exists for this customer.",
  },
  [ClientContextErrorCodes.CREATE_IN_PROGRESS]: {
    key: "iam.onboarding.errors.saveInProgress",
    defaultValue: "Another save for this customer is still running. Try again in a minute.",
  },
  [ClientContextErrorCodes.COMP_MAPPING_CONFLICT]: {
    key: "iam.onboarding.errors.compMappingConflict",
    defaultValue: "This competitor partition is already linked to another customer.",
  },
  [ClientContextErrorCodes.SETUP_OWNED_BY_OTHER_TENANT]: {
    key: "iam.onboarding.errors.setupOwnedByOtherOrg",
    defaultValue: "This customer's data setup belongs to another organization.",
  },
  [ClientContextErrorCodes.CUSTOMER_NOT_CONFIGURED]: {
    key: "iam.onboarding.errors.customerNotConfigured",
    defaultValue: "Set the Customer partition on the Organization tab before adding client onboarding.",
  },
  [ClientContextErrorCodes.MULTIPLE_CUSTOMER_NAMES]: {
    key: "iam.onboarding.errors.multipleCustomers",
    defaultValue: "Customer partition lists more than one BigQuery customer. Client onboarding needs exactly one.",
  },
  [ClientContextErrorCodes.WRITES_DISABLED]: {
    key: "iam.onboarding.writesOff",
    defaultValue: "Editing is turned off on this deployment. You can view the saved onboarding, but changes can't be saved yet.",
  },
  [ClientContextErrorCodes.NO_CHANGES]: {
    key: "iam.onboarding.errors.noChanges",
    defaultValue: "There are no changes to save.",
  },
};

/**
 * What the user sees when a save fails: a field validation message stays beside that field, a known
 * situation gets its plain message, and anything else gets `fallbackMessage`. Any other worker text
 * is never shown, because it can name projects, tables, rows and internal checks.
 */
export function toOnboardingSaveError(err: unknown, fallbackMessage: string, t: TFunction): OnboardingSaveError {
  if (err instanceof TenantOnboardingJobError || err instanceof AmberWorkerFailureError) {
    if (err.field && err.code && FIELD_MESSAGE_CODES.has(err.code)) return { message: err.message, field: err.field };
    const known = err.code ? USER_FACING_SAVE_ERRORS[err.code] : undefined;
    if (!known) return { message: fallbackMessage, isGeneral: true };
    return { message: t(known.key, known.defaultValue), ...(err.field ? { field: err.field } : {}) };
  }
  if (err instanceof ApiError && err.status === 400 && err.field && err.code && FIELD_MESSAGE_CODES.has(err.code)) {
    return { message: withoutFieldPrefix(err.message, err.field), field: err.field };
  }
  const apiMessage = getLocalUserFacingApiMessage(err);
  return apiMessage ? { message: apiMessage } : { message: fallbackMessage, isGeneral: true };
}

/** Contracts validation messages read `website_url: Must be an http(s) URL`; beside the field, the name is noise. */
function withoutFieldPrefix(message: string, field: string): string {
  const prefix = `${field}: `;
  return message.startsWith(prefix) ? message.slice(prefix.length) : message;
}

/** The general error to show, unless the rejected field is on screen and carries the reason itself. */
export function onboardingSaveErrorBanner(
  saveError: OnboardingSaveError | null,
  inlineFields: readonly string[],
): string | null {
  if (!saveError) return null;
  return saveError.field && inlineFields.includes(saveError.field) ? null : saveError.message;
}
